/**
 * TTS统一Store (Pinia)
 * 整合TTS转换核心逻辑和免费TTS服务状态管理
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import WebStore from './web-store';
import {
  DEFAULT_LOCAL_TTS_CONFIG,
  checkServerConnection as apiCheckServerConnection,
} from '@/utils/data-adapter';
import type { AppError } from '@/utils/notify';
import { buildAppError, ErrorCode } from '@/utils/notify';

// 创建Web存储实例
const store = new WebStore();

// 定义输入模式常量
export const INPUT_MODE = {
  TEXT: "0",
  SSML: "1"
} as const;

// 表单配置接口
export interface FormConfig {
  languageSelect: string;
  voiceSelect: string;
  voiceStyleSelect: string;
  voiceRoleSelect?: string; // 角色扮演
  role: string;
  speed: number;
  pitch: number;
  volume: number; // 音量（0.1-2.0）
  intensity?: string;
  silence?: string;
  api: number;
  formatType?: string; // ✅ 新增
  autoPreview?: boolean;
  enableSubtitle?: boolean; // ✅ 开启字幕
  subtitleFormat?: 'srt' | 'vtt'; // ✅ 字幕格式
}

// TTS配置接口
export interface TTSConfig {
  enabled: boolean;
  baseUrl: string;
  defaultVoice: string;
  defaultLanguage: string;
  retryCount: number;
  retryInterval: number;
  defaultAudioFormat: string;
  autoPlay: boolean;
}

// 服务器状态接口
export interface ServerStatus {
  connected: boolean;
  lastChecked: number | null;
  freeLimit: any | null;
}

// 音频状态接口
export interface AudioStatus {
  isPlaying: boolean;
  currentUrl: string | null;
}

export const useTTSStore = defineStore('tts', () => {
  // ============= TTS配置状态 =============
  
  const config = ref<TTSConfig>({
    enabled: store.get('localTTS.enabled') ?? true,
    baseUrl: store.get('localTTS.baseUrl') ?? DEFAULT_LOCAL_TTS_CONFIG.baseUrl,
    defaultVoice: store.get('localTTS.defaultVoice') ?? DEFAULT_LOCAL_TTS_CONFIG.defaultVoice,
    defaultLanguage: store.get('localTTS.defaultLanguage') ?? DEFAULT_LOCAL_TTS_CONFIG.defaultLanguage,
    retryCount: store.get('localTTS.retryCount') ?? DEFAULT_LOCAL_TTS_CONFIG.retryCount,
    retryInterval: store.get('localTTS.retryInterval') ?? DEFAULT_LOCAL_TTS_CONFIG.retryInterval,
    defaultAudioFormat: store.get('localTTS.defaultAudioFormat') ?? DEFAULT_LOCAL_TTS_CONFIG.defaultAudioFormat,
    autoPlay: store.get('localTTS.autoPlay') ?? DEFAULT_LOCAL_TTS_CONFIG.autoPlay,
  });

  const serverStatus = ref<ServerStatus>({
    connected: false,
    lastChecked: null,
    freeLimit: null
  });

  const audioStatus = ref<AudioStatus>({
    isPlaying: false,
    currentUrl: null
  });

  // 统一的错误状态
  const error = ref<AppError | null>(null);

  // 输入状态
  const inputs = ref({
    inputValue: '',
    ssmlValue: '',
    isSSMLManuallyEdited: false
  });

  // SSML模式状态
  const isSSMLMode = ref(false);

  // 页面状态
  const page = ref({
    currentPage: 'main',
    asideIndex: '1',  // 默认显示控制面板
    tabIndex: INPUT_MODE.TEXT as string  // 默认文本模式
  });

  // 加载状态
  const isLoading = ref(false);

  // 表单配置状态
  const formConfig = ref<FormConfig>({
    languageSelect: 'zh-CN',
    voiceSelect: 'zh-CN-XiaoxiaoNeural',
    voiceStyleSelect: 'general',
    voiceRoleSelect: undefined, // 角色扮演（可选）
    role: 'default',
    speed: 1.0,
    pitch: 0, // 音调偏移（Hz，-250 到 500）
    volume: 1.0, // 音量（0.1-2.0）
    intensity: 'normal',
    silence: '0ms',
    api: 5, // 默认使用免费TTS服务
    formatType: 'mp3', // ✅ 新增
    autoPreview: false,
    enableSubtitle: false,
    subtitleFormat: 'srt'
  });

  // 当前转换任务
  const currentTask = ref<any>(null);
  const isConverting = ref(false);
  const conversionProgress = ref(0);

  // ============= 计算属性 =============
  
  const isServerConnected = computed(() => serverStatus.value.connected);

  const hasError = computed(() => error.value !== null);

  const canConvert = computed(() =>
    config.value.enabled &&
    isServerConnected.value &&
    !isConverting.value
  );

  // ============= Actions =============
  
  /**
   * 更新TTS配置
   */
  const updateConfig = (newConfig: Partial<TTSConfig>) => {
    config.value = { ...config.value, ...newConfig };
    
    // 保存到本地存储
    Object.entries(newConfig).forEach(([key, value]) => {
      store.set(`localTTS.${key}`, value);
    });
  };
  
  /**
   * 更新表单配置
   */
  const updateFormConfig = (newFormConfig: Partial<FormConfig>) => {
    formConfig.value = { ...formConfig.value, ...newFormConfig };
  };
  
  /**
   * 检查服务器连接
   */
  const checkServerConnection = async () => {
    try {
      const result = await apiCheckServerConnection();

      serverStatus.value = {
        connected: result,
        lastChecked: Date.now(),
        freeLimit: null
      };

      if (!result) {
        setError(buildAppError({
          code: ErrorCode.NETWORK,
          message: '无法连接到服务器'
        }));
      } else {
        clearError();
      }

      return result;
    } catch (err: any) {
      serverStatus.value = {
        connected: false,
        lastChecked: Date.now(),
        freeLimit: null
      };

      setError(buildAppError({
        code: ErrorCode.NETWORK,
        message: err.message || '连接检查失败',
        cause: err
      }));

      throw err;
    }
  };
  
  /**
   * 开始TTS转换
   */
  const startConversion = async (text: string, options?: Partial<FormConfig>) => {
    if (!canConvert.value) {
      throw new Error('当前无法进行转换');
    }
    
    try {
      isConverting.value = true;
      conversionProgress.value = 0;
      
      const convertConfig = { ...formConfig.value, ...options };
      
      // 创建转换任务
      currentTask.value = {
        id: Date.now().toString(),
        text,
        config: convertConfig,
        status: 'processing',
        createdAt: new Date().toISOString()
      };
      
      // 这里应该调用实际的TTS转换API
      // const result = await ttsApi.convert(text, convertConfig);
      
      // 模拟转换进度
      const progressInterval = setInterval(() => {
        conversionProgress.value += 10;
        if (conversionProgress.value >= 100) {
          clearInterval(progressInterval);
          conversionProgress.value = 100;
          
          // 转换完成
          if (currentTask.value) {
            currentTask.value.status = 'completed';
            currentTask.value.audioUrl = 'mock-audio-url';
          }
        }
      }, 200);
      
      return currentTask.value;
    } catch (error: any) {
      if (currentTask.value) {
        currentTask.value.status = 'failed';
        currentTask.value.error = error.message;
      }
      throw error;
    } finally {
      isConverting.value = false;
    }
  };
  
  /**
   * 播放音频
   */
  const playAudio = (url: string) => {
    audioStatus.value = {
      isPlaying: true,
      currentUrl: url
    };
  };

  /**
   * 停止音频播放
   */
  const stopAudio = () => {
    audioStatus.value = {
      isPlaying: false,
      currentUrl: null
    };
  };

  /**
   * 设置错误
   */
  const setError = (err: AppError) => {
    error.value = err;
  };

  /**
   * 清除错误
   */
  const clearError = () => {
    error.value = null;
  };
  
  /**
   * 初始化TTS状态
   */
  const initialize = async () => {
    try {
      // 初始化配置
      console.log('TTS Store 初始化完成');
    } catch (error: any) {
      console.warn('TTS Store 初始化失败:', error.message);
    }
  };

  /**
   * 设置SSML模式
   */
  const setSSMLMode = (mode: boolean) => {
    isSSMLMode.value = mode;
    page.value.tabIndex = mode ? INPUT_MODE.SSML : INPUT_MODE.TEXT;
  };

  /**
   * 设置SSML值并生成完整的SSML格式
   */
  const setSSMLValue = (text: string, generateComplete: boolean = false) => {
    if (generateComplete) {
      // 生成完整的SSML格式
      const voice = formConfig.value.voiceSelect || 'zh-CN-XiaoxiaoNeural';
      const style = formConfig.value.voiceStyleSelect || 'Default';
      const rate = formConfig.value.speed ? `${((formConfig.value.speed - 1) * 100).toFixed(0)}%` : '0%';
      const pitch = formConfig.value.pitch ? `${((formConfig.value.pitch - 1) * 50).toFixed(0)}%` : '0%';

      let ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="https://www.w3.org/2001/mstts" xml:lang="zh-CN">
  <voice name="${voice}">`;

      // 添加表达样式（如果不是默认样式）
      if (style && style !== 'Default') {
        ssml += `
    <mstts:express-as style="${style}">`;
      }

      // 添加韵律控制
      ssml += `
      <prosody rate="${rate}" pitch="${pitch}">
        ${text}
      </prosody>`;

      // 关闭表达样式标签
      if (style && style !== 'Default') {
        ssml += `
    </mstts:express-as>`;
      }

      ssml += `
  </voice>
</speak>`;

      inputs.value.ssmlValue = ssml;
    } else {
      // 直接设置SSML值
      inputs.value.ssmlValue = text;
    }
  };



  /**
   * 重置所有状态
   */
  const reset = () => {
    currentTask.value = null;
    isConverting.value = false;
    conversionProgress.value = 0;
    clearError();
    stopAudio();
  };

  return {
    // 状态
    config,
    serverStatus,
    audioStatus,
    error,
    inputs,
    isSSMLMode,
    page,
    isLoading,
    formConfig,
    currentTask,
    isConverting,
    conversionProgress,

    // 计算属性
    isServerConnected,
    hasError,
    canConvert,

    // 方法
    initialize,
    updateConfig,
    updateFormConfig,
    setSSMLMode,
    setSSMLValue,
    checkServerConnection,
    startConversion,
    playAudio,
    stopAudio,
    setError,
    clearError,
    reset
  };
});
